import config from '@/config';
import { isFeatureOn } from '@/core/growthbook';

export class Policy {
  id: number;
  key: string;
  name: string;
  slug: string;

  constructor({ id, key, name, slug }: { id: number; key: string; name: string; slug: string }) {
    this.id = id;
    this.key = key;
    this.name = name;
    this.slug = slug;
  }

  get url(): string {
    return `${config.mainSiteUrl}/${this.slug}`;
  }

  get apiKey(): string {
    return `${this.key}:${this.id}`;
  }
}

export const policies: Readonly<Record<string, Policy>> = Object.fromEntries(
  [
    new Policy({
      id: 6848,
      key: 'terms-of-use',
      name: 'Terms of Use',
      slug: 'policies/onfrontiers-terms-of-use',
    }),
    new Policy({
      id: 6630,
      key: 'terms-of-service',
      name: 'Terms of Service',
      slug: 'policies/terms-of-service',
    }),
    new Policy({
      id: 6864,
      key: 'privacy',
      name: 'Privacy Policy',
      slug: 'policies/privacy-policy',
    }),
    new Policy({
      // ENG-5570. Rev 2.1, effective 11 Aug 2026. The id is ours to mint and is
      // not a Webflow page id: publishing over the same page does not change it,
      // so the date is what makes this value one no existing record holds.
      id: 20260811,
      key: 'expert-participation-agreement',
      name: 'Expert Participation Agreement',
      slug: 'policies/expert-participation-agreement',
    }),
    new Policy({
      // ENG-5570. Link target only, never an acceptance key, so the id is
      // unused. Placeholder rather than a real CMS page id, same convention as
      // the SMS entry below.
      id: 9001,
      key: 'expert-payment-policy',
      name: 'Expert Payment Policy',
      slug: 'policies/expert-payment-policy',
    }),
    new Policy({
      id: 6907,
      key: 'marketplace-expert-agreement',
      name: 'Marketplace Expert Agreement',
      slug: 'policies/marketplace-expert-agreement',
    }),
    new Policy({
      id: 6879,
      key: 'customer-charging-policy',
      name: 'Customer Charging Policy',
      // /policies/customer-charging-policy serves the legacy 2024 policy; the
      // canonical Rev 2.1 path is /policies/charging-policy (ENG-5559).
      slug: 'policies/charging-policy',
    }),
    new Policy({
      id: 7001, // Random ID for SMS Opt-In Agreement
      key: 'sms-opt-in-agreement',
      name: 'SMS Opt-In Agreement',
      slug: '',
    }),
  ].map((p) => [p.key, p])
);

/**
 * The Expert Participation Agreement version experts are currently required to
 * have accepted. `null` means no version is required and the gate is inert.
 *
 * ENG-5570. LIVE. Rev 2.1 published 2026-08-11, so this is non-null and the
 * gate fires: every active expert not holding exactly this string is sent to
 * /legal/accept before any authenticated route renders, roughly 55k people
 * (measured 7 Aug). Setting this back to null is the revert, and is the
 * incident brake of record — see config.disableAgreementGate for why the env
 * var is not.
 *
 * OnFrontiers/api#1760 must be deployed FIRST. UpdateAgreement rejects any
 * policy string absent from compliancesRulesMap, so a webapp-first deploy fails
 * acceptance for every active expert on a screen with no exit. It also breaks
 * expert signup, which writes this same string through Policy.apiKey.
 *
 * The `:id` suffix is ours, not Webflow's. Nothing fetches it from the CMS and
 * nothing validates it. 6917 was a page id once and had been copied forward
 * since 2023, while the document itself was edited (it read "Last Updated:
 * June 25, 2026") without the id moving. The id identifies nothing on its own:
 * we mint it, and it means something only because both repos agree on it.
 *
 * Which is why Rev 2.1 uses the effective date, 20260811, rather than an
 * incremented integer: publishing Rev 2.1 over the existing Webflow page does
 * NOT change 6917, so had the id stayed put, every current holder would still
 * have matched and the gate would have fired for nobody. A new version has to
 * be a value no existing record holds. Policy.id is a number, so a numeric date
 * fits with no type change, and it makes the stored policy string say when the
 * expert agreed to what. Any future revision must follow the same rule.
 *
 * Derived from the Policy rather than repeating the literal, so bumping one
 * without the other cannot leave the gate requiring a version nobody is asked
 * to accept. agreementGate.test.ts asserts the two agree and pins the id, so a
 * future revision cannot move half of this silently.
 *
 * Verify with the coverage query after deploying: if it does not return
 * essentially the whole active expert population, the string is wrong.
 *
 * Not an env var on purpose. A default-off flag is exactly how the 2018 gate
 * stayed inert unnoticed for years; a null constant shows up in a diff.
 */
export const REQUIRED_EPA_VERSION: string | null =
  policies['expert-participation-agreement'].apiKey;

/**
 * GrowthBook key for the runtime kill switch. Boolean, default OFF, no
 * targeting rules.
 *
 * ENG-5601. ON means the gate is OFF: the key names what is switched on, not
 * what the gate is doing. That way round on purpose, because `isOn` returns
 * false for a flag that has not fetched yet, does not exist, or whose CDN is
 * unreachable — and under this polarity all three read as "not disabled", so
 * the gate runs. Named `epa-gate-enabled` instead, those same three failures
 * would silently release a legal consent gate and nobody would notice for
 * weeks. That is the 2018 gate's failure restated in flag form.
 *
 * No targeting rules, because there are no attributes to target on:
 * growthbook.setAttributes is only called from LocationListener, which is
 * commented out in App.tsx. A percentage or attribute rule would match nobody
 * while reading as configured on the dashboard.
 */
export const EPA_GATE_KILL_SWITCH = 'disable-epa-gate';

/**
 * Whether the EPA gate is currently stood down, by either brake.
 *
 * Two brakes, ORed, on purpose:
 *   - `config.disableAgreementGate` is compiled in, so applying it costs an
 *     Amplify rebuild. It survives GrowthBook being the broken thing.
 *   - `EPA_GATE_KILL_SWITCH` is a dashboard toggle that reaches open sessions
 *     in seconds. It is what makes this a 5-minute incident rather than a
 *     40-minute one.
 *
 * Neither replaces `REQUIRED_EPA_VERSION = null`, which remains the brake of
 * record: it is the one that shows up in a diff. See
 * docs/runbooks/epa-gate-kill-switch.md for the order to reach for them in.
 *
 * Reaches further than the name suggests. `hasAccepted` below also serves
 * Dashboard.tsx, which checks terms-of-use and privacy for every viewer rather
 * than experts alone, so standing the gate down also suppresses the dashboard's
 * Terms of Use and Privacy alert for customers and admins. Inherited from
 * `config.disableAgreementGate`, which short-circuited the same method long
 * before the flag existed. Left as-is because narrowing it would change
 * behaviour the env var has always had, but it is a release-everything switch
 * rather than an EPA-only one, and the incident runbook should read that way.
 */
export function isAgreementGateDisabled(): boolean {
  return config.disableAgreementGate || isFeatureOn(EPA_GATE_KILL_SWITCH);
}

/**
 * The effective date Rev 2.1 states, as the expert should read it.
 *
 * Shown on the acceptance screen so the expert can tell which revision they are
 * being asked to accept, and when it took effect.
 *
 * Not what the arbitration opt-out is measured from. That is
 * EPA_OPT_OUT_DEADLINE below, and the two are months apart on purpose.
 *
 * Must match the date printed on the published agreement, not our deploy date.
 * Set it in the same change as REQUIRED_EPA_VERSION above.
 */
export const EPA_EFFECTIVE_DATE: string | null = 'August 11, 2026';

/**
 * The date by which an expert must opt out of the arbitration provision.
 *
 * Two dates do two jobs. August 11 is the document effective date above, and
 * binds new registrations, who accept at signup. This is the opt-out deadline
 * for the incumbent cohort, the experts registered before August 11 who are
 * bound by email notice rather than at signup.
 *
 * An incumbent's own effective date is the earlier of their acceptance at this
 * gate or 14 days from receipt of the notice email (ENG-5597), and their opt-out
 * window is 30 days from that. So this constant is a single floor standing in
 * for ~60k per-recipient dates, and it has to be at or beyond all of them. The
 * notice send finishes by September 10, worst case effective September 24, hence
 * October 24. If the send slips past September 10, this date has to move too.
 *
 * A floor, not a countdown: the same date for every incumbent however early
 * they accept, and it only ever moves later. Someone accepting at the gate after
 * late September gets acceptance plus 30 days instead, which is why the copy
 * carries "or within 30 days of your acceptance if that is later" and why
 * nothing on the screen needs to expire.
 *
 * Do not derive it from EPA_EFFECTIVE_DATE. An earlier draft said "within 30
 * days of the effective date", which resolves to September 10 and understates
 * the window by six weeks.
 *
 * Was October 1, when the incumbent cohort had a fixed September 1 amendment
 * date. That date is gone: forcing 60k emails out 14 days ahead of it would have
 * meant a send steep enough to get deferred by Gmail. Extending this only ever
 * helps recipients, including the dozen who accepted under the October 1 copy.
 *
 * The email names no date at all, deliberately. It can say "30 days from your
 * effective date", which is exact per person, where this screen cannot.
 */
export const EPA_OPT_OUT_DEADLINE = 'October 24, 2026';

export interface Agreement {
  policy: string;
  updated_at: string;
  accepted: boolean;
}

/**
 * The shape the generated Viewer actually hands us: every field optional and
 * nullable, entries themselves possibly null.
 *
 * Declared rather than suppressed. Callers used to reach for `@ts-ignore` here,
 * which hid the fact that the looseness is real: `compliances.agreements` is a
 * hand-editable JSON column, so a missing `policy` is a state the data can be
 * in, not a quirk of codegen. `isAgreementEntry` narrows this to `Agreement`.
 */
export type AgreementLike = {
  policy?: string | null;
  updated_at?: string | null;
  accepted?: boolean | null;
} | null;

export function matchAgreement(apiKey: string, policy: Policy): boolean {
  const [key, id] = apiKey.split(':');
  if (id) {
    return policy.id.toString() === id;
  }
  return policy.key === key;
}

// compliances.agreements is a hand-editable JSON column, so an entry can be
// null or missing `policy` without anything upstream complaining. Both of the
// readers below run inside route loaders, where a throw renders the error page
// on every gated route at once rather than failing one component.
function isAgreementEntry(agreement: AgreementLike | undefined): agreement is Agreement {
  return !!agreement && typeof agreement.policy === 'string';
}

function isValidAgreement(agreement: Agreement) {
  const apiKey = agreement.policy;
  const [key, id] = apiKey.split(':');
  return !!id || !policies[key];
}

function hasAcceptedAgreement(key: string, agreements: Agreement[]) {
  const policy = policies[key];
  if (!policy || !agreements) {
    return false;
  }

  const agreement = agreements.find((a) => matchAgreement(a.policy, policy));
  return !!agreement && agreement.accepted;
}

export class Agreements {
  // Always a list, whatever arrived. The readers below can filter it without
  // null guards precisely because the untrusted shape is dealt with once, in
  // the constructor.
  private readonly userAgreements: readonly AgreementLike[];

  // The PARAMETER is nullable by contract, not by accident: the generated
  // Viewer type allows it, and a user with no compliance row genuinely has
  // none. Declaring that honestly is what lets callers drop their type
  // suppressions.
  //
  // It is not trustworthy beyond that, either. `compliances.agreements` is
  // hand-editable JSON and nothing upstream validates its shape, so the column
  // can hold something that is not a list at all. `.filter` on a non-array
  // throws, and both readers run inside route loaders, so that throw takes out
  // every gated route AND /legal/accept itself: a lockout with no screen left
  // to escape from. Normalising here rather than guarding at each reader means
  // an unusable column reads as "nothing recorded", which sends the expert to
  // the gate to accept again. Deliberately not a bypass — asking again is
  // self-healing, and skipping a legal gate on the strength of a malformed row
  // would not be (ENG-5598).
  constructor(userAgreements: readonly AgreementLike[] | null | undefined) {
    this.userAgreements = Array.isArray(userAgreements) ? userAgreements : [];
  }

  hasAccepted(...policyKeys: string[]): boolean {
    if (isAgreementGateDisabled()) {
      return true;
    }
    // isAgreementEntry still earns its place after the constructor's
    // normalisation: that guarantees a list, not what is in it. Entries can be
    // null or missing `policy` in the same hand-edited column.
    const agreements = this.userAgreements.filter(isAgreementEntry).filter(isValidAgreement);
    return policyKeys.reduce((acc, key) => {
      return acc && hasAcceptedAgreement(key, agreements);
    }, true);
  }

  /**
   * Whether this exact policy string has been accepted.
   *
   * Distinct from hasAccepted, which resolves the key through `policies` and so
   * answers "have they accepted whatever the current Policy.id says". That is
   * the right question at signup and the wrong one for the gate: it makes
   * REQUIRED_EPA_VERSION a boolean, since the version actually compared comes
   * from Policy.id instead. Bumping one without the other then fails silently
   * in both directions. Comparing the string given here means the gate requires
   * exactly the version the constant names, or nothing at all.
   */
  hasAcceptedVersion(policyString: string): boolean {
    if (isAgreementGateDisabled()) {
      return true;
    }
    return this.userAgreements
      .filter(isAgreementEntry)
      .some((a) => a.policy === policyString && a.accepted);
  }
}

export default function agreements(
  userAgreements: readonly AgreementLike[] | null | undefined
): Agreements {
  return new Agreements(userAgreements);
}
